import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  User, 
  Bell, 
  Shield, 
  Palette,
  Globe,
  Camera,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    kpiAlerts: true,
    payrollAlerts: true,
    trainingReminders: true
  });

  const handleSaveProfile = () => {
    toast.success('Profile updated successfully!');
  };

  const handleSaveNotifications = () => {
    toast.success('Notification preferences saved!');
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Settings
        </h1>
        <p className="text-slate-500">Manage your account preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="profile" data-testid="tab-profile">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Profile Information</CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar */}
              <div className="flex items-center gap-6">
                <div className="relative">
                  <Avatar className="w-24 h-24">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-600 text-2xl font-bold">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <Button 
                    size="icon" 
                    className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-indigo-600"
                  >
                    <Camera className="w-4 h-4 text-white" />
                  </Button>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    {user?.first_name} {user?.last_name}
                  </h3>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                  <p className="text-sm text-slate-500 capitalize">{user?.role}</p>
                </div>
              </div>

              <Separator />

              {/* Form */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input defaultValue={user?.first_name} data-testid="input-firstname" />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input defaultValue={user?.last_name} data-testid="input-lastname" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input defaultValue={user?.email} type="email" data-testid="input-email" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input defaultValue={user?.phone} data-testid="input-phone" />
                </div>
                <div className="space-y-2">
                  <Label>Employee ID</Label>
                  <Input defaultValue={user?.employee_id} disabled className="bg-slate-50" />
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Input defaultValue={user?.department} disabled className="bg-slate-50" />
                </div>
              </div>

              <Button 
                onClick={handleSaveProfile}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Notification Preferences</CardTitle>
              <CardDescription>Choose how you want to be notified</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Email Notifications</p>
                    <p className="text-sm text-slate-500">Receive updates via email</p>
                  </div>
                  <Switch 
                    checked={notifications.email}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email: checked }))}
                    data-testid="switch-email"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Push Notifications</p>
                    <p className="text-sm text-slate-500">Receive browser notifications</p>
                  </div>
                  <Switch 
                    checked={notifications.push}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, push: checked }))}
                    data-testid="switch-push"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">KPI Alerts</p>
                    <p className="text-sm text-slate-500">Get notified about performance changes</p>
                  </div>
                  <Switch 
                    checked={notifications.kpiAlerts}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, kpiAlerts: checked }))}
                    data-testid="switch-kpi"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Payroll Alerts</p>
                    <p className="text-sm text-slate-500">Notifications about salary and payslips</p>
                  </div>
                  <Switch 
                    checked={notifications.payrollAlerts}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, payrollAlerts: checked }))}
                    data-testid="switch-payroll"
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">Training Reminders</p>
                    <p className="text-sm text-slate-500">Reminders for pending training</p>
                  </div>
                  <Switch 
                    checked={notifications.trainingReminders}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, trainingReminders: checked }))}
                    data-testid="switch-training"
                  />
                </div>
              </div>

              <Button 
                onClick={handleSaveNotifications}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-notifications-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Preferences
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Security Settings</CardTitle>
              <CardDescription>Manage your account security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <Input type="password" placeholder="Enter current password" data-testid="input-current-password" />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input type="password" placeholder="Enter new password" data-testid="input-new-password" />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input type="password" placeholder="Confirm new password" data-testid="input-confirm-password" />
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="font-medium text-slate-900 mb-4">Two-Factor Authentication</h4>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">2FA Status</p>
                    <p className="text-sm text-slate-500">Add an extra layer of security</p>
                  </div>
                  <Button variant="outline" data-testid="enable-2fa-btn">
                    Enable 2FA
                  </Button>
                </div>
              </div>

              <Button 
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="change-password-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
